import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { ShoppingCart, CreditCard, Mail, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const navigate = useNavigate();
  const { cart, getCartSubtotal, getCartVAT, getCartTotalWithVAT, clearCart } = useCart();
  const { user } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    customer_name: user?.name || '',
    customer_email: user?.email || '',
    customer_phone: '',
    delivery_address: '',
    notes: '',
    payment_method: 'email'
  });

  if (cart.length === 0) {
    return (
      <div className="min-h-screen pt-32 pb-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <ShoppingCart size={64} className="mx-auto text-slate-300 mb-4" />
            <h2 className="text-3xl font-bold text-slate-800 mb-4">Carrello Vuoto</h2>
            <p className="text-slate-600 mb-6">Aggiungi prodotti al carrello per procedere con l'ordine</p>
            <Button onClick={() => navigate('/prodotti')} className="bg-orange-600 hover:bg-orange-700">
              Vai ai Prodotti
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const orderItems = cart.map(item => ({
        product_id: item.product_id,
        product_name: item.name,
        quantity: item.quantity,
        price: item.price || 0,
        discount_percentage: item.discount_percentage || 0
      }));

      const orderData = {
        ...formData,
        items: orderItems
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        withCredentials: true
      });

      toast.success('Richiesta d\'ordine inviata con successo! Francesco Valerio ti contatterà a breve.');
      clearCart();
      navigate('/', { state: { orderSuccess: true } });

    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'invio della richiesta d\'ordine');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-32 pb-20 bg-slate-50">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl font-bold text-slate-800 mb-4 text-center">Richiesta d'Ordine</h1>
        <p className="text-center text-slate-600 mb-8 max-w-3xl mx-auto">
          Per completare l'acquisto, compila il modulo sottostante. La tua richiesta verrà inviata al 
          <span className="font-semibold"> tecnico titolare Francesco Valerio</span> che ti contatterà per confermare 
          l'ordine e discutere i dettagli di logistica, consegna e pagamento.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="sticky top-32">
              <CardHeader>
                <CardTitle>Riepilogo Ordine</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.map((item) => {
                    const finalPrice = (item.price || 0) * (1 - (item.discount_percentage || 0) / 100);
                    const quantityMultiplier = item.unit_type === 'bancali' ? 54 : 1;
                    const totalSacchi = item.quantity * quantityMultiplier;
                    
                    return (
                      <div key={`${item.product_id}-${item.unit_type}`} className="flex gap-3 pb-4 border-b">
                        <img
                          src={item.image_url || 'https://via.placeholder.com/60'}
                          alt={item.name}
                          className="w-16 h-16 object-cover rounded"
                        />
                        <div className="flex-1">
                          <h4 className="font-semibold text-sm">{item.name}</h4>
                          <p className="text-xs text-slate-600">{item.brand}</p>
                          {item.unit_type === 'bancali' ? (
                            <p className="text-xs text-blue-600 font-semibold mt-1">
                              {item.quantity} Bancali ({totalSacchi} sacchi)
                            </p>
                          ) : (
                            <p className="text-xs text-slate-600 mt-1">
                              {item.quantity} sacchi
                            </p>
                          )}
                          <p className="text-sm mt-1">
                            <span className="font-semibold">€{(finalPrice * quantityMultiplier).toFixed(2)}</span>
                            <span className="text-slate-600"> x {item.quantity}</span>
                          </p>
                        </div>
                      </div>
                    );
                  })}

                  <div className="pt-4 space-y-2">
                    <div className="flex justify-between text-slate-700">
                      <span>Subtotale (IVA esclusa):</span>
                      <span className="font-semibold">€{getCartSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-slate-700">
                      <span>IVA (22%):</span>
                      <span className="font-semibold">€{getCartVAT().toFixed(2)}</span>
                    </div>
                    <div className="border-t-2 border-slate-300 pt-2 mt-2">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Totale IVA inclusa:</span>
                        <span className="text-orange-600">€{getCartTotalWithVAT().toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Dati Cliente</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="customer_name">Nome e Cognome *</Label>
                    <Input
                      id="customer_name"
                      name="customer_name"
                      value={formData.customer_name}
                      onChange={handleChange}
                      required
                      placeholder="Mario Rossi"
                      className="mt-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="customer_email">Email *</Label>
                      <Input
                        id="customer_email"
                        name="customer_email"
                        type="email"
                        value={formData.customer_email}
                        onChange={handleChange}
                        required
                        placeholder="mario@example.com"
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="customer_phone">Telefono *</Label>
                      <Input
                        id="customer_phone"
                        name="customer_phone"
                        type="tel"
                        value={formData.customer_phone}
                        onChange={handleChange}
                        required
                        placeholder="333 1234567"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="delivery_address">Indirizzo di Consegna *</Label>
                    <Textarea
                      id="delivery_address"
                      name="delivery_address"
                      value={formData.delivery_address}
                      onChange={handleChange}
                      required
                      rows={3}
                      placeholder="Via Roma 123, 76017 San Ferdinando di Puglia (BT)"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="notes">Note Aggiuntive</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      rows={3}
                      placeholder="Eventuali richieste speciali..."
                      className="mt-2"
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Modalità di Conferma</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-3 p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <Mail className="text-blue-600 mt-1 flex-shrink-0" size={24} />
                    <div>
                      <p className="font-semibold text-slate-800 mb-2">Richiesta d'Ordine via Email</p>
                      <p className="text-sm text-slate-700 mb-2">
                        La tua richiesta verrà inviata a <span className="font-semibold">Francesco Valerio</span> 
                        (edilvale.valerio@gmail.com) che ti contatterà per:
                      </p>
                      <ul className="text-sm text-slate-700 space-y-1 ml-4 list-disc">
                        <li>Confermare la disponibilità dei prodotti</li>
                        <li>Discutere le modalità di consegna e logistica</li>
                        <li>Concordare il metodo di pagamento</li>
                        <li>Fornire un preventivo dettagliato</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Button
                type="submit"
                size="lg"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-orange-600 to-orange-500 hover:from-orange-700 hover:to-orange-600 text-white text-lg py-6"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 animate-spin" size={20} />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    <Mail className="mr-2" size={20} />
                    Invia Richiesta d'Ordine - €{getCartTotalWithVAT().toFixed(2)}
                  </>
                )}
              </Button>
              
              <p className="text-sm text-center text-slate-600 mt-4">
                Cliccando su "Invia Richiesta d'Ordine" riceverai una conferma via email e sarai contattato 
                dal nostro tecnico entro 24 ore lavorative.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
